<i18n>
{
  "fr": {
    "indicator-general-last-update": "Dernière mise à jour:",
    "projects-list-title": "Conformité par projet",
    "general-conformity-page-title": "Conformité générale",
    "indicator-template-status": "Statut du gabarit",
    "indicator-requirements": "Exigence(s)"       
  },
  "en": {
    "indicator-general-last-update": "Last update:",
    "projects-list-title": "Project compliance",
    "general-conformity-page-title": "General conformity",
    "indicator-template-status": "Template status",
    "indicator-requirements": "Requirement(s)"
  }
}
</i18n>
<template>
  <div>
    <s-loader :active="!indicator && $apollo.queries.infos.loading" />
    <prismic-content
      v-if="indicator"
      :doctype="DOCUMENT_TYPE_INDICATOR"
      :uid="indicator.prismicUid"
      v-slot="{ content }"
      @loaded="onPrismicContentLoaded()"
    >
      <!-- Message avisant que l'indicator n'est pas éditable -->
      <prismic-content v-if="readOnly" :doctype="$consts.prismic.DOCUMENT_TYPE_INDICATOR_DETAIL" v-slot="{ content }">
        <b-message
          v-if="content && $prismicText(content.information_comes_template_message)"
          type="is-info"
          icon-size="is-medium"
          has-icon
          class="has-background-white mb-24px"
        >
          <prismic-rich-text :field="content.information_comes_template_message" />
        </b-message>
      </prismic-content>

      <!-- Heading -->
      <indicator-heading
        v-if="loaded.prismic && !indicator.isNonCompliances"
        :surtitle="currentIndicatorGroupName"
        :title="$prismicText(content.title)"
        :isSpecific="indicator.isSpecific"
      >
        <template #actions="{ isSticky }">
          <p v-if="indicator.updatedAt && !isSticky" class="is-size-7 has-max-width-768 mb-3">
            {{ $t('indicator-general-last-update') }} {{ $format.dateTime(indicator.updatedAt) }}
          </p>
          <div :class="[{ 'has-text-right': $media.tablet }, { 'is-flex indicator-heading__actions': $media.widescreen }]">
            <indicator-project-switch
              :indicator="indicator"
              :project="project"
              :isSpecific="indicator.isSpecific"
              :currentBusinessId="currentBusinessId"
            />
            <toggle-specific-indicator
              v-if="showToggleSpecificIndicator"
              :indicator="indicator"
              :projectId="project.id"
              :class="{
                'mt-8px': $media.mobile,
                'mt-12px mr-0': $media.tablet && !$media.widescreen,
                'mt-0 mr-12px': $media.widescreen
              }"
              @on-set-specific="(indicatorId) => navigateToIndicator(indicatorId)"
              @on-remove-specific="(templateIndicatorId) => navigateToIndicator(templateIndicatorId)"
            />
          </div>
        </template>
      </indicator-heading>
      <div class="card card--no-top-border-radius">
        <!-- Édition des paramètres de l'indicateur -->
        <hr class="is-marginless" />

        <indicator-editor :indicator="indicator" v-if="!indicator.isNonCompliances" :readOnly="readOnly" />
        <ui-box :class="{ 'pt-32px': $media.tablet }">
          <div :class="{ 'is-flex': $media.tablet }">
            <!-- Description des exigences -->
            <div class="has-max-width-768">
              <h2 class="title is-4 has-text-weight-bold mt-0">{{ $t('indicator-requirements') }}</h2>
              <prismic-rich-text class="content is-custom has-text-black-ter is-size-6" :field="content.description" />
            </div>
            <!--Statut au niveau du Gabarit-->
            <div v-if="indicator.isSpecific" class="template-status">
              <h2 class="is-size-7 is-uppercase has-text-weight-medium mb-2">{{ $t('indicator-template-status') }}</h2>
              <div class="template-status__badge">
                <template-status-badge v-if="indicatorTemplate" :status="indicatorTemplate.status" />
                <ui-tooltip
                  v-if="indicator.relatedIndicatorTemplate && indicator.relatedIndicatorTemplate.updatedAt"
                  :label="$t('indicator-general-last-update')"
                  :content="$format.dateTime(indicator.relatedIndicatorTemplate.updatedAt)"
                  :iconSpacing="8"
                ></ui-tooltip>
              </div>
            </div>
          </div>
        </ui-box>

        <hr class="m-0" />

        <ui-box :class="{ 'pt-4px': $media.mobile, 'pt-24px': $media.tablet, 'pt-40px': $media.fullhd }">
          <b-tabs :value="getActiveTab(content)" @input="(tab) => (selectedTab = tab)">
            <!-- Aide -->
            <indicator-tab-general-help :indicator="indicator" :tabName="TAB_NAME_HELP" />

            <!-- Note de suivi -->
            <indicator-tab-follow-up-note
              :indicator="indicator"
              :tabName="TAB_NAME_NOTE"
              :isSpecific="indicator.isSpecific"
              :readOnly="readOnly"
              @onSaveIndicatorNote="saveIndicatorNote"
            />

            <!-- Informations pour l'auditeur -->
            <indicator-tab-auditor-infos
              :indicator="indicator"
              :tabName="TAB_NAME_AUDITOR"
              :isSpecific="indicator.isSpecific"
              :readOnly="readOnly"
              @onSaveAuditorNote="saveAuditorNote"
              @onSaveResponsibleNote="saveResponsibleNote"
            />
          </b-tabs>
        </ui-box>
      </div>

      <!-- Procédures -->
      <div class="card mt-24px">
        <ui-box>
          <indicator-procedures
            :indicator="indicator"
            :readOnly="readOnly || !canBeModifiedProcedure"
            :indicatorTemplate="indicatorTemplate"
            @onSaveProcedure="saveProcedure"
            @onClearProcedure="clearProcedure"
            @ready="loaded.procedures = true"
          />
        </ui-box>
      </div>

      <!-- Suivi de documents à fournir -->
      <div class="card mt-24px">
        <ui-box>
          <document-list
            :indicator="indicator"
            :indicator-content="content"
            :readOnly="readOnly"
            @documentsMutated="onDocumentsMutated"
            @ready="loaded.documentList = true"
            class="has-max-width-768"
          />
        </ui-box>
        <div v-if="showPreviousNext" class="is-hidden-mobile">
          <hr class="is-marginless" />
          <ui-box class="py-24px">
            <sequence-nav-bar
              :previous="previousIndicator && { label: previousIndicator.title, to: previousIndicatorRoute }"
              :next="nextIndicator && { label: nextIndicator.title, to: nextIndicatorRoute }"
            ></sequence-nav-bar>
          </ui-box>
        </div>
      </div>
    </prismic-content>
  </div>
</template>

<script>
import { INDICATOR_STATUS_NOT_APPLICABLE } from '@/consts/enums'
import BusinessService from '@/services/business'
import { sendSignal } from '@/services/signals'
import i18n from '@/helpers/i18n'

import { DOCUMENT_TYPE_INDICATOR } from '@/consts/prismic'
import { URL_GENERAL_COMPLIANCE, URL_PROJECT_COMPLIANCE, URL_PROJECT_LIST } from '@/consts/urls'

import MutationHelper from '@/graphql/local/mutation-helper'

import PrismicContent from '@/components/prismic/prismic-content'
import UiBox from '@/components/ui/box'
import Breadcrumbs from '@/mixins/breadcrumbs'
import IndicatorHeading from '@/components/indicator/indicator-heading'
import IndicatorEditor from '@/components/indicator/indicator-editor'
import IndicatorProcedures from '@/components/indicator/indicator-procedures'
import IndicatorProjectSwitch from '@/components/indicator/indicator-project-switch'
import IndicatorTabAuditorInfos from '@/components/indicator/indicator-tab-auditor-infos'
import IndicatorTabFollowUpNote from '@/components/indicator/indicator-tab-follow-up-note'
import IndicatorTabGeneralHelp from '@/components/indicator/indicator-tab-general-help'
import DocumentList from '@/components/indicator/document-list'
import SequenceNavBar from '@/components/generic/sequence-nav-bar'
import TemplateStatusBadge from '@/components/indicator/template-status-badge'
import ToggleSpecificIndicator from '@/components/indicator/toggle-specific-indicator'
import UiTooltip from '@/components/ui/tooltip'

const TAB_NAME_AUDITOR = 'auditor'
const TAB_NAME_HELP = 'help'
const TAB_NAME_NOTE = 'note'

export default {
  mixins: [Breadcrumbs],
  components: {
    PrismicContent,
    UiBox,
    IndicatorHeading,
    IndicatorEditor,
    IndicatorProcedures,
    IndicatorTabAuditorInfos,
    IndicatorTabFollowUpNote,
    IndicatorTabGeneralHelp,
    IndicatorProjectSwitch,
    DocumentList,
    SequenceNavBar,
    TemplateStatusBadge,
    ToggleSpecificIndicator,
    UiTooltip
  },
  data() {
    return {
      loading: false,
      me: null,
      indicator: null,
      currentBusinessId: null,
      DOCUMENT_TYPE_INDICATOR,
      TAB_NAME_AUDITOR,
      TAB_NAME_HELP,
      TAB_NAME_NOTE,
      selectedTab: null,
      business: null,
      project: null,
      loaded: {
        prismic: false,
        infos: false,
        documentList: false,
        procedures: false
      }
    }
  },
  created() {
    this.currentBusinessId = BusinessService.getCurrentBusiness().id
  },
  computed: {
    indicatorId() {
      return this.$route.params.id
    },
    projectId() {
      return this.$route.params.projectId
    },
    isProjectIndicator() {
      return !!this.projectId
    },
    breadcrumbs() {
      if (!this.indicator) return []

      if (this.isProjectIndicator) {
        if (!this.project) return []

        return [
          {
            label: this.$t('projects-list-title'),
            url: { name: URL_PROJECT_LIST }
          },
          {
            label: this.project.name,
            url: { name: URL_PROJECT_COMPLIANCE, query: { from: this.indicatorId } }
          },
          {
            label: this.indicator.title
          }
        ]
      } else {
        return [
          {
            label: this.$t('general-conformity-page-title'),
            url: { name: URL_GENERAL_COMPLIANCE, query: { from: this.indicatorId } }
          },
          {
            label: this.indicator.title
          }
        ]
      }
    },
    currentIndicatorGroupName() {
      if (!this.complianceData || !this.complianceData.indicatorGroups) return null

      const group = this.complianceData.indicatorGroups.find((g) => g.indicators.find((i) => i.id === this.indicatorId))
      if (group) {
        return group.groupName
      }
      return null
    },
    complianceData() {
      if (this.project) return this.project.compliance
      if (this.business) return this.business.compliance
      return null
    },
    complianceIndicators() {
      if (!this.complianceData || !this.complianceData.indicatorGroups) return null

      const indicators = this.complianceData.indicatorGroups.flatMap((x) => x.indicators)

      return indicators
    },
    showPreviousNext() {
      return this.indicator && !this.indicator.isNonCompliances && (this.previousIndicator || this.nextIndicator)
    },
    previousIndicator() {
      if (!this.complianceIndicators) return null

      var currentIndicatorIndex = this.complianceIndicators.map((x) => x.id).lastIndexOf(this.indicatorId)

      if (this.indicatorId && currentIndicatorIndex > 0) {
        return this.complianceIndicators[currentIndicatorIndex - 1]
      }

      return null
    },
    nextIndicator() {
      if (!this.complianceIndicators) return null

      var currentIndicatorIndex = this.complianceIndicators.map((x) => x.id).lastIndexOf(this.indicatorId)

      if (this.indicatorId && currentIndicatorIndex < this.complianceIndicators.length - 1) {
        return this.complianceIndicators[currentIndicatorIndex + 1]
      }

      return null
    },
    previousIndicatorRoute() {
      if (!this.previousIndicator) return null

      return this.isProjectIndicator
        ? {
            name: this.$consts.urls.URL_INDICATOR_PROJECT,
            params: { projectId: this.projectId, id: this.previousIndicator.id }
          }
        : {
            name: this.$consts.urls.URL_INDICATOR_GENERAL,
            params: { id: this.previousIndicator.id }
          }
    },
    nextIndicatorRoute() {
      if (!this.nextIndicator) return null

      return this.isProjectIndicator
        ? {
            name: this.$consts.urls.URL_INDICATOR_PROJECT,
            params: { projectId: this.projectId, id: this.nextIndicator.id }
          }
        : {
            name: this.$consts.urls.URL_INDICATOR_GENERAL,
            params: { id: this.nextIndicator.id }
          }
    },
    showToggleSpecificIndicator() {
      if (!this.isProjectIndicator) return false
      if (this.project.isProjectTemplate) return false
      return true
    },
    readOnly() {
      if (!this.isProjectIndicator) return false
      if (this.project.isProjectTemplate) return false
      return !this.indicator.isSpecific
    },
    isNotApplicable() {
      return this.indicator.status === INDICATOR_STATUS_NOT_APPLICABLE
    },
    canBeModifiedProcedure() {
      if (!this.isProjectIndicator) return true
      if (this.project.isProjectTemplate) return true
      return false
    },
    indicatorTemplate() {
      if (!this.isProjectIndicator) return this.indicator
      if (this.project.isProjectTemplate) return this.indicator
      if (!this.indicator.isSpecific) return this.indicator
      return this.indicator.relatedIndicatorTemplate
    }
  },
  methods: {
    onPrismicContentLoaded() {
      this.loaded.prismic = true
    },
    onDocumentsMutated(args) {
      const cache = args.cache
      const action = args.action
      const data = args.data
      const query = this.$getGraphQLQuery(this.$options.apollo.infos)

      switch (action) {
        case 'added':
          MutationHelper.updateCache(cache, query, (x) => {
            x.indicator.documents.push(data)
          })
          break

        case 'updated':
          MutationHelper.updateCache(cache, query, (x) => {
            const updatedDoc = x.indicator.documents.find((d) => d.id == data.id)
            if (data.isDone !== undefined) updatedDoc.isDone = data.isDone
            if (data.notes !== undefined) updatedDoc.notes = data.notes
            if (data.title !== undefined) updatedDoc.title = data.title
            if (data.link !== undefined) updatedDoc.link = data.link
            if (data.content !== undefined) updatedDoc.content = data.content
            if (data.contentHtml !== undefined) updatedDoc.contentHtml = data.contentHtml
          })
          break

        case 'deleted':
          MutationHelper.updateCache(cache, query, (x) => {
            x.indicator.documents = x.indicator.documents.filter((d) => d.id !== data.id)
          })
          break
      }
    },
    async saveIndicatorNote(args) {
      await this.$apollo.mutate({
        mutation: this.$options.mutation.UpdateIndicator,
        variables: {
          input: {
            indicatorId: this.indicator.id,
            language: i18n.contentLanguage(),
            notes: { value: args }
          }
        }
      })
      this.$success()
    },
    async saveAuditorNote(value, saveCallback) {
      await this.$apollo.mutate({
        mutation: this.$options.mutation.UpdateIndicator,
        variables: {
          input: {
            indicatorId: this.indicator.id,
            language: i18n.contentLanguage(),
            auditorNote: { value: value }
          }
        }
      })
      this.$success()

      if (saveCallback) saveCallback()
    },
    async saveResponsibleNote(value, saveCallback) {
      await this.$apollo.mutate({
        mutation: this.$options.mutation.UpdateIndicator,
        variables: {
          input: {
            indicatorId: this.indicator.id,
            language: i18n.contentLanguage(),
            responsibleNote: { value: value }
          }
        }
      })
      this.$success()

      if (saveCallback) saveCallback()
    },
    async saveProcedure(value, saveCallback) {
      await this.$apollo.mutate({
        mutation: this.$options.mutation.UpdateOfficialProcedure,
        variables: {
          indicatorId: this.indicator.id,
          language: i18n.contentLanguage(),
          officialProcedure: value
        }
      })
      this.$success()

      if (saveCallback) saveCallback()
    },
    async clearProcedure() {
      await this.$apollo.mutate({
        mutation: this.$options.mutation.UpdateOfficialProcedure,
        variables: {
          indicatorId: this.indicator.id,
          language: i18n.contentLanguage(),
          officialProcedure: null
        }
      })
    },

    getActiveTab(helpContent) {
      const defaultTab = helpContent.drawers.length > 0 ? TAB_NAME_HELP : TAB_NAME_NOTE
      return this.selectedTab || defaultTab
    },
    navigateToIndicator(indicatorId) {
      this.$router.push({
        name: this.$consts.urls.URL_INDICATOR_PROJECT,
        params: { projectId: this.projectId, id: indicatorId }
      })
    }
  },
  apollo: {
    infos: {
      query() {
        return this.$withFragments(
          this.$options.query.Indicator_Infos,
          IndicatorEditor.fragments.IndicatorEditorFields,
          DocumentList.fragments.DocumentListFields,
          DocumentList.fragments.UpdatedAtDocumentFields
        )
      },
      variables() {
        return {
          businessId: this.currentBusinessId,
          indicatorId: this.indicatorId,
          language: i18n.contentLanguage()
        }
      },
      skip() {
        return !this.currentBusinessId
      },
      update(data) {
        this.me = data.me
        this.indicator = data.indicator
        this.loaded.infos = true
        if (this.selectedTab === null) {
          this.selectedTab = this.indicator.notes && this.indicator.notes.trim() ? TAB_NAME_NOTE : null
        }
      }
    },
    project: {
      query() {
        return this.$options.query.Indicator_ProjectName
      },
      variables() {
        return {
          projectId: this.projectId
        }
      },
      skip() {
        return !this.isProjectIndicator
      },
      fetchPolicy: 'cache-first'
    },
    businessCompliance: {
      query() {
        return this.$options.query.BusinessCompliance
      },
      variables() {
        return {
          businessId: this.currentBusinessId,
          language: i18n.contentLanguage()
        }
      },
      update(data) {
        this.business = data.business
      },
      skip() {
        return !this.currentBusinessId || this.isProjectIndicator
      },
      fetchPolicy: 'cache-first'
    },
    projectCompliance: {
      query() {
        return this.$options.query.ProjectCompliance
      },
      variables() {
        return {
          projectId: this.projectId,
          language: i18n.contentLanguage()
        }
      },
      update(data) {
        this.project = data.project
      },
      skip() {
        return !this.isProjectIndicator
      },
      fetchPolicy: 'cache-first'
    }
  },
  watch: {
    loaded: {
      handler(value) {
        if (value.prismic && value.infos && value.documentList && value.procedures) {
          // Le setTimeout donne le temps à une transition de l'entête de se compléter
          setTimeout(() => sendSignal('scroll'), 500)
        }
      },
      deep: true
    },
    'indicator.id'() {
      if (this.isProjectIndicator) {
        // refresh apollo queries
        this.$apollo.queries.projectCompliance.refetch()
      }
    }
  }
}
</script>

<graphql>
query Indicator_ProjectName($projectId: ID!) {
  project(id: $projectId) {
    id
    name
  }
}

query Indicator_Infos($businessId: ID!, $indicatorId: ID!, $language: ContentLanguage!) {
  me {
    id
    userBusiness(businessId: { value: $businessId }) {
      id
      userType
    }
  }

  indicator(id: $indicatorId, language: $language) {
    id
    language
    title
    prismicUid
    documents {
      id
      ...DocumentListFields
      ...UpdatedAtDocumentFields
    }
    notes
    auditorNote
    responsibleNote
    updatedAt
    isSpecific
    officialProcedure

    ...IndicatorEditorFields

    ... on GeneralIndicatorGraphType {
      isNonCompliances
    }

    ... on ProjectIndicatorGraphType {
      project {
        id
        name
      }
      relatedIndicatorTemplate {
        id
        language
        title
        status
        updatedAt
        project {
          id
          name
        }
      }
    }
  }
}

query BusinessCompliance($businessId: ID!, $language: ContentLanguage!) {
  business(id: $businessId) {
    id
    compliance(language: $language) {
      indicatorGroups {
        groupName
        indicators {
          id
          language
          title
        }
      }
    }
  }
}

query ProjectCompliance($projectId: ID!, $language: ContentLanguage!) {
  project(id: $projectId) {
    id
    name
    isProjectTemplate
    compliance(language: $language) {
      indicatorGroups {
        groupName
        indicators {
          id
          language
          title
          isSpecific
        }
      }
    }
  }
}

mutation UpdateIndicator($input: UpdateIndicatorInput!) {
  updateIndicator(input: $input) {
    indicator {
      id
      language
      notes
      auditorNote
      responsibleNote
      updatedAt
    }
    project {
      id
      updatedAt
      updatedBy {
        id
        profile {
          id
          firstName
          lastName
        }
      }
    }
  }
}

mutation UpdateOfficialProcedure($indicatorId: ID!, $language: ContentLanguage!, $officialProcedure: String) {
  updateIndicator(input: { indicatorId: $indicatorId, language: $language, officialProcedure: { value: $officialProcedure } }) {
    indicator {
      id
      language
      officialProcedure
    }
  }
}
</graphql>

<style lang="scss" scoped>
.indicator-heading {
  &__actions {
    flex-direction: row-reverse;
  }
}

.template-status {
  margin-top: $space-24px;

  @include tablet {
    margin-right: 0;
    margin-left: auto;
    margin-top: 0;
    padding-left: $space-24px;
  }

  &__badge {
    white-space: nowrap;
  }
}
</style>